import { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/Layout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AlertTriangle, Phone, Search, IndianRupee, Clock, ArrowUpDown, ExternalLink, Filter } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
};

export default function RedAlerts({ user }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [threshold, setThreshold] = useState(150000);
  const [sortBy, setSortBy] = useState('overdue');
  const [count, setCount] = useState(0);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/red-alerts?threshold=${threshold}`);
      setAlerts(res.data.alerts || []);
      setCount(res.data.count || 0);
    } catch {
      toast.error('Failed to fetch red alerts');
    } finally {
      setLoading(false);
    }
  }, [threshold]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const filtered = alerts.filter(a =>
    (a.customer_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (a.mobile_number || '').includes(search) ||
    (a.property_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (a.booking_number || '').toLowerCase().includes(search.toLowerCase())
  );

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'overdue') return b.overdue_installments - a.overdue_installments || a.total_paid - b.total_paid;
    if (sortBy === 'lowest') return a.total_paid - b.total_paid;
    if (sortBy === 'highest_balance') return b.balance_due - a.balance_due;
    return 0;
  });

  const overdueCount = alerts.filter(a => a.overdue_installments > 0).length;

  return (
    <Layout user={user}>
      <div className="space-y-5" data-testid="red-alerts-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800 flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertTriangle size={20} className="text-red-600" />
              </div>
              Red Alerts - Telecalling
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {count} customers with payment below {formatCurrency(threshold)}
              {overdueCount > 0 && <span className="text-red-600 font-medium"> | {overdueCount} overdue</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 bg-white border rounded-lg px-3 py-2">
              <span className="text-xs text-slate-500 whitespace-nowrap">Threshold:</span>
              <select
                data-testid="threshold-select"
                value={threshold}
                onChange={e => setThreshold(Number(e.target.value))}
                className="text-sm font-medium bg-transparent border-none focus:outline-none"
              >
                <option value={50000}>50K</option>
                <option value={100000}>1 Lakh</option>
                <option value={150000}>1.5 Lakh</option>
                <option value={200000}>2 Lakh</option>
                <option value={300000}>3 Lakh</option>
                <option value={500000}>5 Lakh</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <p className="text-xs text-red-600 font-medium">Total Alerts</p>
              <p className="text-2xl font-bold text-red-700" data-testid="total-alerts-count">{count}</p>
            </CardContent>
          </Card>
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-4">
              <p className="text-xs text-orange-600 font-medium">Overdue</p>
              <p className="text-2xl font-bold text-orange-700">{overdueCount}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 font-medium">Total Pending</p>
              <p className="text-xl font-bold text-slate-700">{formatCurrency(alerts.reduce((s, a) => s + a.balance_due, 0))}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 font-medium">Total Collected</p>
              <p className="text-xl font-bold text-emerald-600">{formatCurrency(alerts.reduce((s, a) => s + a.total_paid, 0))}</p>
            </CardContent>
          </Card>
        </div>

        {/* Search + Sort */}
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="red-alert-search"
              placeholder="Search by name, mobile, property, booking..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex items-center gap-1.5 bg-white border rounded-lg px-3 py-2 shrink-0">
            <ArrowUpDown size={14} className="text-slate-400" />
            <select
              data-testid="sort-select"
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="text-sm bg-transparent border-none focus:outline-none"
            >
              <option value="overdue">Overdue First</option>
              <option value="lowest">Lowest Payment</option>
              <option value="highest_balance">Highest Balance</option>
            </select>
          </div>
        </div>

        {/* Alerts List */}
        {loading ? (
          <div className="text-center py-16">
            <div className="w-8 h-8 border-2 border-red-300 border-t-red-600 rounded-full animate-spin mx-auto mb-3" />
            <p className="text-slate-500 text-sm">Loading alerts...</p>
          </div>
        ) : sorted.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border">
            <AlertTriangle size={40} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 font-medium">
              {search ? 'No matching results' : 'No red alerts found'}
            </p>
            <p className="text-slate-400 text-sm mt-1">
              {search ? 'Try a different search term' : `All customers have paid above ${formatCurrency(threshold)}`}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {sorted.map((alert) => (
              <Card key={alert.booking_id} data-testid={`alert-card-${alert.booking_id}`}
                className={`overflow-hidden transition-all hover:shadow-md ${
                  alert.overdue_installments > 0 ? 'border-red-300 bg-red-50/50' : 'border-orange-200 bg-orange-50/30'
                }`}>
                <CardContent className="p-0">
                  <div className="flex flex-col sm:flex-row">
                    {/* Left: Priority indicator */}
                    <div className={`w-full sm:w-1.5 h-1.5 sm:h-auto shrink-0 ${
                      alert.overdue_installments > 0 ? 'bg-red-500' : 'bg-orange-400'
                    }`} />

                    <div className="flex-1 p-4 sm:p-5">
                      <div className="flex flex-col sm:flex-row justify-between gap-3">
                        {/* Customer Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                            <h3 className="font-bold text-base text-slate-800 truncate">{alert.customer_name}</h3>
                            {alert.overdue_installments > 0 && (
                              <span className="px-2 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded-full uppercase shrink-0">
                                {alert.overdue_installments} Overdue
                              </span>
                            )}
                            <span className="text-[10px] text-slate-400 font-mono shrink-0">#{alert.booking_number}</span>
                          </div>

                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
                            <span className="font-medium">{alert.property_name}</span>
                            {alert.property_size && <span className="text-slate-400">| {alert.property_size}</span>}
                          </div>

                          {/* Payment Progress */}
                          <div className="mt-3">
                            <div className="flex justify-between text-xs mb-1.5">
                              <span className="text-slate-500">Paid: <span className="font-semibold text-emerald-600">{formatCurrency(alert.total_paid)}</span></span>
                              <span className="text-slate-500">Balance: <span className="font-semibold text-red-600">{formatCurrency(alert.balance_due)}</span></span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${
                                  alert.total_paid === 0 ? 'bg-red-500' :
                                  alert.total_paid < alert.total_price * 0.3 ? 'bg-orange-500' : 'bg-emerald-500'
                                }`}
                                style={{ width: `${Math.min(100, alert.total_price > 0 ? (alert.total_paid / alert.total_price) * 100 : 0)}%` }}
                              />
                            </div>
                            <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                              <span>Total: {formatCurrency(alert.total_price)}</span>
                              {alert.next_due_date && (
                                <span className="flex items-center gap-1">
                                  <Clock size={10} /> Next Due: {alert.next_due_date}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex sm:flex-col items-center sm:items-end gap-2 shrink-0">
                          <a href={`tel:${alert.mobile_number}`}
                            data-testid={`call-btn-${alert.booking_id}`}
                            className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 transition-all active:scale-95 shadow-sm">
                            <Phone size={16} />
                            <span className="hidden sm:inline">Call</span>
                            <span>{alert.mobile_number}</span>
                          </a>
                          <a href={`https://wa.me/91${(alert.mobile_number || '').replace(/[^0-9]/g, '').slice(-10)}`}
                            target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#25D366] text-white text-sm font-medium rounded-lg hover:bg-[#1fb855] transition-all active:scale-95 shadow-sm">
                            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                            WhatsApp
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
